/**
 * Caprion Rejection Screen
 * 
 * Shows when a loan application has been rejected.
 * Blocks all navigation and shows rejection reason.
 */

import React from 'react';
import { XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CaprionRejectionScreenProps {
  reason?: string;
  timestamp?: string | null;
  context?: 'aa_verification' | 'bureau' | 'general'; // Context to customize message
}

const CaprionRejectionScreen: React.FC<CaprionRejectionScreenProps> = ({
  reason = 'Your loan application has been rejected by the lender',
  timestamp,
  context = 'general'
}) => {
  const handleReturnHome = () => {
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    window.location.href = '/contact-support';
  };

  // Get context-specific message for info box
  const getInfoBoxMessage = () => {
    switch (context) {
      case 'aa_verification':
        return 'Unfortunately, your loan application could not be approved based on the bank statement data shared through Account Aggregator. You cannot proceed with this application. Please contact support for more information or start a new application.';
      case 'bureau':
        return 'Unfortunately, your loan application could not be approved based on your credit bureau report. You cannot proceed with this application. Please contact support for more information or start a new application.';
      case 'general':
      default:
        return 'Unfortunately, your loan application has been rejected by the lender. You cannot proceed with this application. Please contact support for more information or start a new application.';
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <div className="bg-white border border-red-300 rounded-2xl shadow-lg p-10 max-w-md w-full">
          <div className="flex flex-col items-center text-center space-y-4">
            {/* Icon */}
            <div className="bg-red-100 rounded-full p-4">
              <XCircle className="h-16 w-16 text-red-600" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900">
              Application Rejected
            </h2>
            
            {/* Timestamp */}
            {timestamp && (
              <p className="text-sm text-gray-500">
                Rejected on {new Date(timestamp).toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            )}
            
            {/* Info Box */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full mt-2">
              <p className="text-sm text-gray-700 leading-relaxed">
                {getInfoBoxMessage()}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-4">
              <Button
                onClick={handleReturnHome}
                variant="outline"
                className="flex-1 py-3 border-gray-300 hover:bg-gray-50"
              >
                Return to Home
              </Button>
              <Button
                onClick={handleContactSupport}
                className="flex-1 py-3 bg-intellend-green hover:bg-intellend-green/90 text-white"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaprionRejectionScreen;
